import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "../axiosInstance";
import "../pages/banktransfer.css";
import logo from "../assets/logo.png";
import { QRCodeCanvas } from "qrcode.react";

const MAX_SIZE_MB = 50;

function QrPaymentPage({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const amount = location.state?.amount ?? 0;
  const method = location.state?.method ?? "sbp_qr";
  const [loading, setLoading] = useState(true);
  const [errorMsgPay, setErrorMsgPay] = useState("");
  const [details, setDetails] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [showUploadError, setShowUploadError] = useState(false);
  const [uploadErrorMsg, setUploadErrorMsg] = useState("");
  const [highlight, setHighlight] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [blockActions, setBlockActions] = useState(false);
  const [copiedMessage, setCopiedMessage] = useState("");
  const uploadRef = useRef();
  const fileInputRef = useRef();
  const actionsRef = useRef();
  const addedIosRef = useRef(false);
  const addedPushRef = useRef(false);

  useEffect(() => {
    const ua = navigator.userAgent || "";
    const isIphone = /iPhone|iPod/.test(ua);
    if (isIphone) {
      document.documentElement.classList.add("ios");
      addedIosRef.current = true;
    } else {
      document.documentElement.classList.add("push-2cm");
      addedPushRef.current = true;
    }
    document.documentElement.classList.add("av-hidden");
    return () => {
      document.documentElement.classList.remove("av-hidden");
      if (addedIosRef.current) document.documentElement.classList.remove("ios");
      if (addedPushRef.current) document.documentElement.classList.remove("push-2cm");
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setErrorMsgPay("");
    axios
      .post("/payments/create/", { amount, method }, { withCredentials: true })
      .then((res) => {
        if (!isMounted) return;
        const data = res.data;
        if (data.detail) {
          setErrorMsgPay(data.detail || "Не удалось получить QR. Попробуйте позже.");
          return;
        }
        setDetails(data);
      })
      .catch(() => {
        setErrorMsgPay("Не удалось получить QR. Попробуйте позже.");
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [amount, method, user]);

  const goBack = () => {
    if (blockActions || submitting) return;
    setBlockActions(true);
    navigate("/card");
    setTimeout(() => setBlockActions(false), 800);
  };

  const copy = async (text) => {
    const val = String(text || "");
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(val);
        ok = true;
      }
    } catch {}
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = val;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        ok = true;
      } catch {}
    }
    if (ok) {
      setCopiedMessage("Ссылка скопирована");
      setTimeout(() => setCopiedMessage(""), 1500);
    }
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setUploadedFile(null);
      setUploadErrorMsg(`Файл слишком большой (максимум ${MAX_SIZE_MB} МБ)`);
      setShowUploadError(true);
      setHighlight(true);
      setTimeout(() => setHighlight(false), 1600);
      setTimeout(() => setShowUploadError(false), 3500);
      setTimeout(() => {
        if (uploadRef.current) {
          uploadRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
      return;
    }
    setUploadedFile(file);
    setUploadErrorMsg("");
    setShowUploadError(false);
    setHighlight(false);
    setTimeout(() => {
      if (actionsRef.current) {
        actionsRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 150);
  };

  const handleConfirm = async () => {
    if (blockActions || submitting) return;
    if (!uploadedFile) {
      setHighlight(true);
      setShowUploadError(true);
      setUploadErrorMsg("Пожалуйста, прикрепите скрин/файл подтверждения перевода");
      setTimeout(() => setHighlight(false), 1600);
      setTimeout(() => setShowUploadError(false), 3500);
      setTimeout(() => {
        if (uploadRef.current) {
          uploadRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
      return;
    }
    setBlockActions(true);
    setHighlight(false);
    setShowUploadError(false);
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("user_id", user?.telegram_id);
      formData.append("pay_id", details.pay_id);
      formData.append("method", method);
      formData.append("amount", details.amount);
      formData.append("file", uploadedFile);
      const res = await axios.post("/project/send-payment-message", formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.status === "ok") {
        navigate("/card", {
          state: { confirmMessage: "✅ Заявка отправлена. Ожидайте подтверждения администратора." },
        });
      } else {
        alert("❌ Не удалось отправить заявку. Повторите позже.");
      }
    } catch {
      alert("⚠️ Ошибка при подтверждении. Попробуйте позже.");
    } finally {
      setSubmitting(false);
      setBlockActions(false);
    }
  };

  const uploadSection = (
    <div ref={uploadRef} className={`upload-section${highlight ? " upload-section-highlight" : ""}`}>
      {showUploadError && (
        <div className="upload-error-block">
          <div className="upload-error-text">
            <svg style={{ width: 22, height: 22, marginRight: 6 }} viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="10" fill="#fecaca" />
              <path d="M10 6v4" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" />
              <circle cx="10" cy="14" r="1.3" fill="#dc2626" />
            </svg>
            {uploadErrorMsg || "Ошибка загрузки файла"}
          </div>
          <div className="upload-error-arrow">
            <svg width="28" height="28" viewBox="0 0 28 28">
              <path d="M14 6v13m0 0-6.5-6.5M14 19л6.5-6.5" stroke="#dc2626" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
      )}
      <label className="upload-label" style={{ width: "100%", display: "block" }}>
        Загрузите скрин/файл перевода:
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*,application/pdf"
          style={{ display: "none" }}
          onChange={(e) => handleFileSelect(e.target.files[0])}
        />
        <div
          className="upload-dropzone"
          tabIndex={0}
          onDrop={(e) => {
            e.preventDefault();
            handleFileSelect(e.dataTransfer.files[0]);
          }}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              fileInputRef.current && fileInputRef.current.click();
            }
          }}
        >
          <div style={{ width: "100%" }}>
            <button
              type="button"
              className="btn-confirm2"
              style={{ width: 195, fontSize: 15, margin: "0 auto 6px auto", display: "block", background: "#8B5CF6" }}
              tabIndex={-1}
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current && fileInputRef.current.click();
              }}
            >
              {uploadedFile ? "Заменить файл" : "Выбрать файл"}
            </button>
            <div style={{ color: "#A5B4FC", fontSize: "14px", opacity: 0.95, marginTop: 3 }}>или перетащите сюда файл</div>
          </div>
        </div>
      </label>
      {uploadedFile && (
        <div className="upload-file-info" style={{ marginTop: 10 }}>
          {uploadedFile.type.startsWith("image/") ? (
            <img src={URL.createObjectURL(uploadedFile)} alt="preview" className="upload-preview" style={{ maxWidth: 160, maxHeight: 120, marginBottom: 8 }} />
          ) : (
            <div className="upload-filename">{uploadedFile.name}</div>
          )}
          <div>
            <span style={{ fontSize: 13, color: "#ccc" }}>({(uploadedFile.size / 1024 / 1024).toFixed(2)} МБ)</span>
            <button className="upload-remove-btn" style={{ marginLeft: 10 }} onClick={() => setUploadedFile(null)}>
              Удалить
            </button>
          </div>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="loader-center">
        <div className="loader-wrapper">
          <div className="loader-logo-wrapper1">
            <img src={logo} alt="Logo" className="loader-logo" />
          </div>
          <div className="glow-circle"></div>
          <div className="loading-text">
            Генерация QR
            <span className="dot-animate">
              <span>.</span>
              <span>.</span>
              <span>.</span>
            </span>
          </div>
          <div className="loader-hинt">Обычно занимает не&nbsp;более 30&nbsp;секунд</div>
        </div>
      </div>
    );
  }

  if (errorMsgPay) {
    return (
      <div className="bank-transfer-container">
        <div className="error-msg-pay">
          {errorMsgPay}
          <div className="error-msg-hint">Попробуйте изменить сумму или способ оплаты.</div>
        </div>
        <div className="transfer-actions" style={{ marginTop: 30 }}>
          <button className="btn-cancel2" onClick={goBack} disabled={blockActions || submitting}>Назад</button>
        </div>
      </div>
    );
  }

  if (!details) return null;
  const qrLink = details.qr_code || details.qr_url || details.page_url || "";

  return (
    <div className="bank-transfer-container">
      {copiedMessage && <div className="copy-toast">{copiedMessage}</div>}
      <div className="transfer-tip">
        Отсканируйте QR-код в приложении банка.
        <div className="transfer-deadline">Время на оплату: 20 минут.</div>
      </div>
      <div className="transfer-field">
        <label>Сумма перевода:</label>
        <div className="transfer-row">
          <span>{details.amount || "-"}</span>
        </div>
      </div>
      <div className="transfer-field" style={{ textAlign: "center", padding: 16 }}>
        {qrLink ? (
          <div className="qr-outer-wrapper">
            <div className="crypto-qr-box">
              <div className="qr-white-padding">
                <QRCodeCanvas value={qrLink} size={180} bgColor="#fff" fgColor="#24c6dc" level="H" includeMargin={false} className="qr-canvas" />
                <div className="qr-logo-circle">
                  <img src={logo} alt="logo" className="qr-logo-img" />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div>QR не получен</div>
        )}
        {qrLink && (
          <div className="transfer-row" style={{ marginTop: 12, columnGap: 10, alignItems: "center" }}>
            <a href={qrLink} target="_blank" rel="noopener noreferrer" style={{ color: "#9cc3db", wordBreak: "break-all", textDecoration: "underline", flex: 1 }}>
              {qrLink}
            </a>
            <button className="copy-btn" onClick={() => copy(qrLink)} aria-label="Скопировать ссылку" title="Скопировать">
              Скопировать
            </button>
          </div>
        )}
      </div>
      {qrLink && (
        <div className="important-note">
          <div className="important-note__title">Важное примечание</div>
          <div className="important-note__text">
            Обратите внимание: оплата оформляется как перевод в формате «чаевых». Для корректной оплаты переведите точную сумму и подтвердите перевод. Несовпадение суммы может привести к задержке обработки или отклонению платежа.
          </div>
        </div>
      )}
      {uploadSection}
      <div className="transfer-actions" ref={actionsRef}>
        <button className="btn-cancel2" onClick={goBack} disabled={blockActions || submitting}>Отмена</button>
        <button className="btn-confirm2" onClick={handleConfirm} disabled={submitting || loading || blockActions}>{submitting ? "Отправка..." : "Подтвердить"}</button>
      </div>
    </div>
  );
}

export default QrPaymentPage;






































